import React, { useState, useMemo, memo } from "react";
import Chart from "react-apexcharts";
import moment from "moment";
import "./RcrLeaderboardReport.css";

// Palette of vibrant, distinct colors assigned to branches on the frontend
const FRONTEND_BRANCH_COLORS = [
  "#2563eb", // Royal Blue
  "#0d9488", // Teal
  "#d97706", // Amber
  "#7c3aed", // Purple
  "#e11d48", // Rose Red
  "#0284c7", // Sky Blue
  "#059669", // Emerald Green
  "#ea580c", // Orange
  "#4f46e5", // Indigo
  "#db2777", // Pink
  "#0891b2", // Cyan
  "#64748b", // Slate
];

const RcrScoreTrendSection = ({
  scoreTrend,
  branchCards,
}) => {
  // Selected branch for interactive focusing
  const [focusedBranchId, setFocusedBranchId] = useState(null);

  // Parse structure (months array with branches) or older structure
  const { windowMonths, branches } = useMemo(() => {
    if (!scoreTrend) {
      return { windowMonths: [], branches: [] };
    }

    const rawMonths = Array.isArray(scoreTrend)
      ? scoreTrend
      : Array.isArray(scoreTrend?.months)
      ? scoreTrend.months
      : [];

    const isNewStructure = rawMonths.length > 0 && Array.isArray(rawMonths[0]?.branches);

    if (isNewStructure) {
      // Helper: parse a month string into a moment-backed descriptor
      const parseMonthDescriptor = (monthStr, isProjected = false, fallbackKey = "") => {
        const str = monthStr || "";
        const m = str ? moment(str, ["MMM-YYYY", "YYYY-MM", "MMM YYYY", "MMMM YYYY", "YYYY-MM-DD"]) : null;
        const isValid = m && m.isValid();
        return {
          rawMonth: str,
          key: isValid ? m.format("YYYY-MM") : fallbackKey,
          label: isValid ? m.format("MMM") : str,
          fullLabel: isValid ? m.format("MMMM YYYY") : str,
          moment: isValid ? m : null,
          is_projected: isProjected,
        };
      };

      // Check which month in rawMonths was the last to have actual branch audit scores
      const monthHasScores = (mItem) => {
        return (mItem?.branches || []).some(
          (b) => b && b.score !== null && b.score !== undefined && !isNaN(parseFloat(b.score))
        );
      };

      let lastAuditedIdx = -1;
      for (let i = rawMonths.length - 1; i >= 0; i--) {
        if (monthHasScores(rawMonths[i])) {
          lastAuditedIdx = i;
          break;
        }
      }

      const explicitProjIdx = rawMonths.findIndex((m) => m?.is_projected === true);

      let histRawMonths = [];
      let projRawMonth = null;

      if (explicitProjIdx !== -1) {
        histRawMonths = rawMonths.slice(0, explicitProjIdx);
        projRawMonth = rawMonths[explicitProjIdx];
      } else if (lastAuditedIdx !== -1 && lastAuditedIdx < rawMonths.length - 1) {
        // Backend already supplied the projected month right after the last audited month (e.g. Nov-2026 with null scores)
        histRawMonths = rawMonths.slice(0, lastAuditedIdx + 1);
        projRawMonth = rawMonths[lastAuditedIdx + 1];
      } else if (lastAuditedIdx !== -1) {
        // The last month in rawMonths has scores; need to generate +1 month for projected
        histRawMonths = rawMonths;
        projRawMonth = null;
      } else {
        histRawMonths = rawMonths;
        projRawMonth = null;
      }

      // 1. Historical months
      const histMonths = histRawMonths.map((mItem, idx) =>
        parseMonthDescriptor(mItem?.month, false, `hist-${idx}`)
      );

      if (histMonths.length === 0 && !projRawMonth) {
        return { windowMonths: [], branches: [] };
      }

      // 2. Projected month (+1 month)
      let projMonth = null;
      if (projRawMonth) {
        projMonth = parseMonthDescriptor(projRawMonth?.month, true, `proj-0`);
      } else if (histMonths.length > 0 && histMonths[histMonths.length - 1].moment) {
        const nextM = histMonths[histMonths.length - 1].moment.clone().add(1, "month");
        projMonth = {
          rawMonth: nextM.format("MMM-YYYY"),
          key: nextM.format("YYYY-MM"),
          label: nextM.format("MMM"),
          fullLabel: nextM.format("MMMM YYYY"),
          moment: nextM,
          is_projected: true,
        };
      } else if (histMonths.length > 0) {
        projMonth = {
          rawMonth: "Projected",
          key: "proj-1",
          label: "Projected",
          fullLabel: "Projected",
          moment: null,
          is_projected: true,
        };
      }

      const winMonths = projMonth ? [...histMonths, projMonth] : histMonths;

      // 3. Extract unique branches preserving initial order
      const branchMap = new Map();
      rawMonths.forEach((mItem) => {
        (mItem?.branches || []).forEach((b) => {
          if (!b || b.branch_id === undefined || b.branch_id === null) return;
          if (!branchMap.has(b.branch_id)) {
            branchMap.set(b.branch_id, {
              branch_id: b.branch_id,
              branch_name: b.branch_name || `Branch ${b.branch_id}`,
              scores_by_month: {},
            });
          }
          const bRec = branchMap.get(b.branch_id);
          const scoreVal =
            b.score !== null && b.score !== undefined && !isNaN(parseFloat(b.score))
              ? Math.round(parseFloat(b.score) * 10) / 10
              : null;

          if (mItem?.month) {
            bRec.scores_by_month[mItem.month] = scoreVal;
            const m = moment(mItem.month, ["MMM-YYYY", "YYYY-MM", "MMM YYYY", "MMMM YYYY", "YYYY-MM-DD"]);
            if (m.isValid()) {
              bRec.scores_by_month[m.format("YYYY-MM")] = scoreVal;
            }
          }
        });
      });

      // 4. Calculate for each branch: historical scores, latest, previous, projected (+1 only)
      const branchList = Array.from(branchMap.values()).map((b) => {
        const histScores = histMonths.map((m) => {
          return b.scores_by_month[m.rawMonth] ?? b.scores_by_month[m.key] ?? null;
        });

        // Collect all non-null historical scores chronologically
        const validScores = [];
        histMonths.forEach((m, idx) => {
          const val = histScores[idx];
          if (val !== null && val !== undefined && !isNaN(val)) {
            validScores.push({ month: m, score: val });
          }
        });

        const hasData = validScores.length > 0;
        let latestScore = null;
        let previousScore = null;
        let projectedScore = null;
        let changeSinceStart = null;
        let trendCode = "new";

        if (validScores.length === 1) {
          latestScore = validScores[0].score;
          previousScore = null;
          trendCode = "new";
        } else if (validScores.length >= 2) {
          latestScore = validScores[validScores.length - 1].score;
          previousScore = validScores[validScores.length - 2].score;
          const delta = latestScore - previousScore;
          changeSinceStart = Math.round(delta * 10) / 10;
          trendCode = delta > 0 ? "improving" : delta < 0 ? "declining" : "stable";
        }

        // Project +1 month score:
        // 1. If backend already supplied a score for the projected month, use it
        const backendProjScore = projMonth
          ? b.scores_by_month[projMonth.rawMonth] ??
            b.scores_by_month[projMonth.key] ??
            b.projected_score
          : b.projected_score;

        const lastHistScore = histScores.length > 0 ? histScores[histScores.length - 1] : null;

        if (
          backendProjScore !== null &&
          backendProjScore !== undefined &&
          !isNaN(parseFloat(backendProjScore))
        ) {
          projectedScore = Math.round(parseFloat(backendProjScore) * 10) / 10;
        } else if (lastHistScore !== null && lastHistScore !== undefined) {
          // Linear projection based on delta from previousScore to latestScore
          if (previousScore !== null && previousScore !== undefined) {
            const delta = latestScore - previousScore;
            projectedScore = Math.min(100, Math.max(0, Math.round((latestScore + delta) * 10) / 10));
          } else {
            projectedScore = latestScore;
          }
        }

        const seriesData = projMonth ? [...histScores, projectedScore] : histScores;

        return {
          branch_id: b.branch_id,
          branch_name: b.branch_name,
          scores: histScores,
          scores_by_month: b.scores_by_month,
          latest_score: latestScore,
          previous_score: previousScore,
          projected_score: projectedScore,
          trend_series: seriesData,
          hasData,
          trend_code: trendCode,
          change_since_start: changeSinceStart,
        };
      });

      // Filter only active branches that have recorded audit data
      const activeBranches = branchList.filter((b) => b.hasData);

      // Assign frontend colors to branches
      const finalized = activeBranches.map((b, idx) => ({
        ...b,
        color: FRONTEND_BRANCH_COLORS[idx % FRONTEND_BRANCH_COLORS.length],
        rank: idx + 1,
      }));

      return {
        windowMonths: winMonths,
        branches: finalized,
      };
    }

    // Older structure fallback (if scoreTrend.branches exists directly with data)
    if (scoreTrend && scoreTrend.branches && Array.isArray(scoreTrend.branches) && scoreTrend.branches.length > 0) {
      const oldBranches = scoreTrend.branches.map((b, idx) => ({
        ...b,
        color: b.color || FRONTEND_BRANCH_COLORS[idx % FRONTEND_BRANCH_COLORS.length],
      }));

      const labels = Array.isArray(scoreTrend.month_labels)
        ? scoreTrend.month_labels
        : Array.isArray(scoreTrend.months)
        ? scoreTrend.months.map((m) => (typeof m === "string" ? m : m.label || m.name || m.month)).filter(Boolean)
        : [];

      if (labels.length === 0) {
        return { windowMonths: [], branches: [] };
      }

      const winMonths = labels.map((lbl, idx) => ({
        rawMonth: lbl,
        key: `old-${idx}`,
        label: lbl,
        fullLabel: lbl,
        is_projected: idx === labels.length - 1,
      }));

      return {
        windowMonths: winMonths,
        branches: oldBranches,
      };
    }

    return { windowMonths: [], branches: [] };
  }, [scoreTrend]);

  const monthLabels = useMemo(() => windowMonths.map((m) => m.label), [windowMonths]);

  // Map of branch_id -> assigned frontend color
  const branchColorMap = useMemo(() => {
    const map = {};
    branches.forEach((b) => {
      map[b.branch_id] = b.color;
    });
    return map;
  }, [branches]);

  // Derived or passed branch KPI cards
  const cards = useMemo(() => {
    if (branchCards && Array.isArray(branchCards) && branchCards.length > 0) {
      return branchCards.map((c, idx) => ({
        ...c,
        color: branchColorMap[c.branch_id] || c.color || FRONTEND_BRANCH_COLORS[idx % FRONTEND_BRANCH_COLORS.length],
      }));
    }

    return branches.map((b) => {
      const latest = b.latest_score;
      const prev = b.previous_score;
      const isNew = prev === null || prev === undefined;
      const diff = !isNew ? Math.round((latest - prev) * 10) / 10 : null;

      let changePtsDisplay = "New";
      if (!isNew) {
        if (diff > 0) {
          changePtsDisplay = `▲ ${diff} pts`;
        } else if (diff < 0) {
          changePtsDisplay = `▼ ${Math.abs(diff)} pts`;
        } else {
          changePtsDisplay = `0 pts`;
        }
      }

      return {
        branch_id: b.branch_id,
        branch_name: b.branch_name,
        rank: b.rank,
        color: b.color,
        overall_score: latest !== null ? `${latest}%` : "-",
        score_raw: latest,
        previous_score: prev,
        previous_score_display: prev !== null ? `${prev}%` : null,
        change_pp: diff !== null ? (diff > 0 ? `+${diff}` : `${diff}`) : null,
        change_pp_raw: diff,
        change_pts_display: changePtsDisplay,
        trend: isNew ? "New" : diff > 0 ? "▲ Improving" : diff < 0 ? "▼ Declining" : "Stable",
        trend_code: b.trend_code,
        rag: latest >= 60 ? "● Green" : latest >= 50 ? "● Amber" : "● Red",
        rag_code: latest >= 60 ? "green" : latest >= 50 ? "amber" : "red",
      };
    });
  }, [branchCards, branches, branchColorMap]);

  // Toggle focus on a branch
  const handleToggleFocus = (branchId) => {
    setFocusedBranchId((prev) => (prev === branchId ? null : branchId));
  };

  // ApexCharts Series for historical months + 1 projected month
  const chartSeries = useMemo(() => {
    return branches.map((b) => {
      const rawName = b.branch_name || "";
      const truncatedName = rawName.length > 15 ? `${rawName.slice(0, 15).trim()}...` : rawName;

      return {
        name: truncatedName,
        data: b.trend_series || b.scores || [],
        color: b.color,
      };
    });
  }, [branches]);

  // Compute dynamic min Y-axis bound
  const yMin = useMemo(() => {
    const allScores = chartSeries.flatMap((s) =>
      (s.data || []).filter((v) => v !== null && v !== undefined && !isNaN(v))
    );
    if (allScores.length === 0) return 30;
    const lowest = Math.min(...allScores);
    return lowest >= 40 ? 30 : Math.max(0, Math.floor(lowest / 10) * 10);
  }, [chartSeries]);

  // ApexChart Options
  const chartOptions = useMemo(() => {
    const hasFocus = focusedBranchId !== null;

    // Calculate stroke widths and opacities based on focus
    const strokeWidths = branches.map((b) =>
      hasFocus ? (b.branch_id === focusedBranchId ? 3.5 : 1.5) : 2.5
    );
    const strokeColors = branches.map((b) => {
      if (hasFocus && b.branch_id !== focusedBranchId) {
        return "rgba(148, 163, 184, 0.25)";
      }
      return b.color || "#3b82f6";
    });

    return {
      chart: {
        type: "line",
        height: 320,
        animations: { enabled: true, easing: "easeinout", speed: 400 },
        toolbar: { show: false },
        background: "transparent",
        zoom: { enabled: false },
      },
      colors: strokeColors,
      stroke: {
        curve: "straight",
        width: strokeWidths,
      },
      forecastDataPoints: {
        count: 1, // Only +1 for projected
        fillOpacity: 0.5,
        strokeWidth: undefined,
        dashArray: 4,
      },
      markers: {
        size: branches.map((b) => (hasFocus ? (b.branch_id === focusedBranchId ? 5 : 0) : 4)),
        strokeColors: "#ffffff",
        strokeWidth: 2,
        hover: { size: 6 },
      },
      xaxis: {
        categories: monthLabels,
        labels: {
          style: {
            colors: "#64748b",
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: {
        min: yMin,
        max: 100,
        tickAmount: 4,
        labels: {
          style: {
            colors: "#64748b",
            fontSize: "12px",
            fontWeight: 600,
          },
          formatter: (val) => `${Math.round(val)}%`,
        },
      },
      grid: {
        show: true,
        borderColor: "#f1f5f9",
        strokeDashArray: 3,
        padding: { top: 0, right: 10, bottom: 0, left: 10 },
      },
      legend: {
        show: false, // Using custom branch pills header instead
      },
      tooltip: {
        theme: "light",
        shared: true,
        intersect: false,
        style: { fontSize: "12px" },
        y: {
          title: {
            formatter: () => "",
          },
          formatter: (val, opts) => {
            if (val === null || val === undefined) return "N/A";
            const seriesIndex = opts?.seriesIndex;
            const dataPointIndex = opts?.dataPointIndex;
            const branch = typeof seriesIndex === "number" ? branches[seriesIndex] : null;
            const rawName =
              branch?.branch_name ||
              (typeof seriesIndex === "number" ? opts?.w?.globals?.seriesNames?.[seriesIndex] : "") ||
              "";
            const isProj =
              typeof dataPointIndex === "number" ? windowMonths[dataPointIndex]?.is_projected : false;
            return `${rawName ? `${rawName}: ` : ""}${val}%${isProj ? " (Projected)" : ""}`;
          },
        },
      },
    };
  }, [branches, monthLabels, windowMonths, focusedBranchId, yMin]);

  // If no scoreTrend or no branches/months to display, do not render anything
  if (!scoreTrend || branches.length === 0 || windowMonths.length === 0) {
    return null;
  }

  return (
    <div className="rcr-score-trend-container mb-4">
      {/* Header */}
      <div className="mb-3">
        <h4 className="rcr-score-trend-title mt-2">Score trend</h4>
        <p className="rcr-score-trend-subtitle">
          Hover or tap the chart to compare branches in any month.
        </p>
      </div>

      {/* Branch Pills Filter / Legend */}
      <div className="rcr-branch-pills-row mb-3">
        {branches.map((b) => {
          const isFocused = focusedBranchId === b.branch_id;
          return (
            <button
              key={b.branch_id}
              type="button"
              className={`rcr-branch-pill-btn ${isFocused ? "active" : ""}`}
              onClick={() => handleToggleFocus(b.branch_id)}
              title={isFocused ? "Click to unfocus" : `Focus on ${b.branch_name}`}
            >
              <span
                className="rcr-branch-pill-dot"
                style={{ backgroundColor: b.color || "#3b82f6" }}
              />
              <span className="rcr-branch-pill-name">{b.branch_name}</span>
            </button>
          );
        })}
        {focusedBranchId !== null && (
          <button
            type="button"
            className="rcr-branch-pill-reset"
            onClick={() => setFocusedBranchId(null)}
          >
            Show all
          </button>
        )}
      </div>

      {/* ApexCharts Line Chart */}
      <div className="rcr-chart-wrapper">
        <Chart
          options={chartOptions}
          series={chartSeries}
          type="line"
          height={320}
          width="100%"
        />
      </div>

      {/* Bottom Row: Branch Cards */}
      <div className="rcr-branch-cards-row mt-4">
        {cards.map((c) => {
          const isFocused = focusedBranchId === c.branch_id;
          const isImproving =
            c.trend_code === "improving" ||
            (c.change_pts_display && c.change_pts_display.includes("▲")) ||
            (c.change_pp_raw && c.change_pp_raw > 0);
          const isDeclining =
            c.trend_code === "declining" ||
            (c.change_pts_display && c.change_pts_display.includes("▼")) ||
            (c.change_pp_raw && c.change_pp_raw < 0);

          return (
            <div
              key={c.branch_id}
              className={`rcr-branch-kpi-card ${isFocused ? "focused" : ""}`}
              onClick={() => {
                handleToggleFocus(c.branch_id);
              }}
              role="button"
              tabIndex={0}
              title={`Click to focus on ${c.branch_name}`}
            >
              {/* Branch Name with colored dot */}
              <div className="rcr-branch-kpi-header">
                <span
                  className="rcr-branch-kpi-dot"
                  style={{ backgroundColor: c.color || "#3b82f6" }}
                />
                <span className="rcr-branch-kpi-name">{c.branch_name}</span>
              </div>

              {/* Score and Change Badge */}
              <div className="rcr-branch-kpi-body">
                <span
                  className="rcr-branch-kpi-score"
                  style={{
                    color:
                      c.rag_code === "red"
                        ? "#dc2626"
                        : c.score_raw >= 60
                        ? "#15803d"
                        : c.score_raw >= 50
                        ? "#d97706"
                        : "#dc2626",
                  }}
                >
                  {c.overall_score || (c.score_raw !== null && c.score_raw !== undefined ? `${c.score_raw}%` : "-")}
                </span>
                <span
                  className={`rcr-branch-kpi-badge ${
                    isImproving ? "badge-up" : isDeclining ? "badge-down" : "badge-neutral"
                  }`}
                >
                  {c.change_pts_display || (c.trend ? c.trend : "New")}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default memo(RcrScoreTrendSection);
